import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import {
  CashShiftDifferenceApprovalSnapshotSchema,
  CashShiftDifferenceTierSchema,
  isCashShiftDifferenceApprovalSnapshot,
} from "./cash-shift-handover.js";
import {
  InventoryOperationTypeSchema,
  type MemberRefundWalletAllocation,
  MemberRefundWalletAllocationsSchema,
} from "./commercial.js";
import {
  GroupBuyDailyReconciliationApprovalSnapshotSchema,
  isGroupBuyDailyReconciliationApprovalSnapshot,
} from "./group-buy-daily-reconciliation.js";
import {
  GroupBuyReconciliationResolutionApprovalSnapshotSchema,
  isGroupBuyReconciliationResolutionApprovalSnapshot,
} from "./group-buy-reconciliation-resolution.js";
import {
  type InventoryCountCapture,
  InventoryCountCaptureSchema,
  isInventoryCountCapture,
} from "./inventory-count.js";
import {
  type InventoryLossCapture,
  InventoryLossCaptureSchema,
  isInventoryLossCapture,
} from "./inventory-loss.js";
import {
  isManualReviewResolutionApprovalSnapshot,
  ManualReviewResolutionApprovalSnapshotSchema,
  manualReviewResolutionCodeMatchesCategory,
} from "./manual-review-resolution.js";
import { isOrderNumber, isOrderNumberForOrderId } from "./order-number.js";
import {
  isPaidOrderItemSaleReturnApprovalBinding,
  type PaidOrderItemSaleReturnApprovalBinding,
  PaidOrderItemSaleReturnApprovalBindingSchema,
  PaidSaleReturnOriginalPaymentsSchema,
  PaidSaleReturnPaymentMethodSchema,
  PaidSaleReturnRefundExecutionModeSchema,
  paidSaleReturnOriginalPayments,
} from "./paid-sale-return.js";
import {
  isPurchaseOrderApprovalSnapshot,
  type PurchaseOrderApprovalSnapshot,
  PurchaseOrderApprovalSnapshotSchema,
} from "./purchase-order-approval.js";
import {
  SaleReturnInventoryEffectSchema,
  SaleReturnPhysicalDispositionSchema,
  saleReturnInventoryEffectForDisposition,
} from "./sale-inventory.js";

const ApprovalIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const ApprovalIdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const ApprovalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\\.[0-9]{3}Z$",
});
const ApprovalReasonSchema = Type.String({ minLength: 2, maxLength: 500 });
const ApprovalSha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const ApprovalMoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const MemberBalanceRefundAmountFenSchema = Type.Integer({ minimum: 1, maximum: 100_000_000 });
const MemberBalanceRefundReasonSchema = Type.String({ minLength: 2, maxLength: 200 });
const InventoryRequestedQuantitySchema = Type.Integer({ minimum: -1_000_000, maximum: 1_000_000 });
const InventoryFactQuantitySchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const InventoryMoneyFenSchema = Type.Integer({ minimum: 0, maximum: 100_000_000 });
const InventoryReasonSchema = Type.String({ minLength: 2, maxLength: 200 });
const InventoryReferenceSchema = Type.Union([
  Type.String({ minLength: 1, maxLength: 80 }),
  Type.Null(),
]);

export const ApprovalRequirementSchema = Type.Union([
  Type.Literal("MANAGER_OR_OWNER"),
  Type.Literal("MANAGER_THEN_OWNER"),
  Type.Literal("OWNER"),
]);
export type ApprovalRequirement = Static<typeof ApprovalRequirementSchema>;

export const ApprovalRequestStatusSchema = Type.Union([
  Type.Literal("PENDING_MANAGER_OR_OWNER"),
  Type.Literal("PENDING_MANAGER"),
  Type.Literal("PENDING_OWNER"),
  Type.Literal("APPROVED"),
  Type.Literal("REJECTED"),
  Type.Literal("CANCELLED"),
]);
export type ApprovalRequestStatus = Static<typeof ApprovalRequestStatusSchema>;

export const ApprovalActorRoleSchema = Type.Union([
  Type.Literal("OWNER"),
  Type.Literal("MANAGER"),
  Type.Literal("CASHIER"),
  Type.Literal("WAITER"),
  Type.Literal("COACH"),
  Type.Literal("SYSTEM"),
]);
export type ApprovalActorRole = Static<typeof ApprovalActorRoleSchema>;

export const ApprovalActorSummarySchema = Type.Object(
  {
    displayName: Type.String({ minLength: 1, maxLength: 80 }),
    role: ApprovalActorRoleSchema,
  },
  { additionalProperties: false },
);
export type ApprovalActorSummary = Static<typeof ApprovalActorSummarySchema>;

export const PreviewOrderCashRefundApprovalRequestSchema = Type.Object(
  {
    orderId: ApprovalIdSchema,
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: ApprovalReasonSchema,
  },
  { additionalProperties: false },
);
export type PreviewOrderCashRefundApprovalRequest = Static<
  typeof PreviewOrderCashRefundApprovalRequestSchema
>;

export const OrderCashRefundApprovalPreviewReadModelSchema = Type.Object(
  {
    kind: Type.Literal("ORDER_CASH_REFUND"),
    orderId: ApprovalIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    tableNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
    orderStatus: Type.Union([Type.Literal("SETTLED"), Type.Literal("REFUNDED")]),
    orderPaidFen: ApprovalMoneyFenSchema,
    orderAlreadyRefundedFen: ApprovalMoneyFenSchema,
    paymentMethod: Type.Literal("CASH"),
    paymentAvailableFen: ApprovalMoneyFenSchema,
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: ApprovalReasonSchema,
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    requirement: ApprovalRequirementSchema,
    approverLabel: Type.String({ minLength: 2, maxLength: 120 }),
    canSubmit: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type OrderCashRefundApprovalPreviewReadModel = Static<
  typeof OrderCashRefundApprovalPreviewReadModelSchema
>;

export const CreateOrderCashRefundApprovalCommandSchema = Type.Object(
  {
    orderId: ApprovalIdSchema,
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: ApprovalReasonSchema,
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateOrderCashRefundApprovalCommand = Static<
  typeof CreateOrderCashRefundApprovalCommandSchema
>;

export const PreviewMemberBalanceRefundApprovalRequestSchema = Type.Object(
  {
    memberId: ApprovalIdSchema,
    originalTransactionId: ApprovalIdSchema,
    amountFen: MemberBalanceRefundAmountFenSchema,
    reason: MemberBalanceRefundReasonSchema,
  },
  { additionalProperties: false },
);
export type PreviewMemberBalanceRefundApprovalRequest = Static<
  typeof PreviewMemberBalanceRefundApprovalRequestSchema
>;

export const MemberBalanceRefundApprovalPreviewReadModelSchema = Type.Object(
  {
    kind: Type.Literal("MEMBER_BALANCE_REFUND"),
    memberId: ApprovalIdSchema,
    memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    transactionOccurredAt: ApprovalDateTimeSchema,
    originalWalletAmountFen: ApprovalMoneyFenSchema,
    alreadyRefundedFen: ApprovalMoneyFenSchema,
    refundableRemainingFen: ApprovalMoneyFenSchema,
    amountFen: MemberBalanceRefundAmountFenSchema,
    reason: MemberBalanceRefundReasonSchema,
    refundAllocations: MemberRefundWalletAllocationsSchema,
    sourceReferenceSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    requirement: ApprovalRequirementSchema,
    approverLabel: Type.String({ minLength: 2, maxLength: 120 }),
    canSubmit: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type MemberBalanceRefundApprovalPreviewReadModel = Static<
  typeof MemberBalanceRefundApprovalPreviewReadModelSchema
>;

export const CreateMemberBalanceRefundApprovalCommandSchema = Type.Object(
  {
    memberId: ApprovalIdSchema,
    originalTransactionId: ApprovalIdSchema,
    amountFen: MemberBalanceRefundAmountFenSchema,
    reason: MemberBalanceRefundReasonSchema,
    sourceReferenceSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateMemberBalanceRefundApprovalCommand = Static<
  typeof CreateMemberBalanceRefundApprovalCommandSchema
>;

const PaidOrderItemReturnRequestFields = {
  orderId: ApprovalIdSchema,
  itemId: ApprovalIdSchema,
  quantity: Type.Integer({ minimum: 1, maximum: 99 }),
  physicalDisposition: SaleReturnPhysicalDispositionSchema,
  reason: MemberBalanceRefundReasonSchema,
};

export const PreviewPaidOrderItemReturnApprovalRequestSchema = Type.Object(
  PaidOrderItemReturnRequestFields,
  { additionalProperties: false },
);
export type PreviewPaidOrderItemReturnApprovalRequest = Static<
  typeof PreviewPaidOrderItemReturnApprovalRequestSchema
>;

export const PaidOrderItemReturnApprovalPreviewReadModelSchema = Type.Object(
  {
    kind: Type.Literal("ORDER_ITEM_PAID_RETURN"),
    itemKind: Type.Optional(Type.Union([Type.Literal("PRODUCT"), Type.Literal("PACKAGE")])),
    lineNumber: Type.Optional(Type.String({ minLength: 1, maxLength: 80 })),
    componentNames: Type.Optional(
      Type.Array(Type.String({ minLength: 1, maxLength: 80 }), {
        minItems: 1,
        maxItems: 30,
      }),
    ),
    orderId: ApprovalIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    orderStatus: Type.Union([Type.Literal("SETTLED"), Type.Literal("REFUNDED")]),
    itemId: ApprovalIdSchema,
    skuId: ApprovalIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    soldQuantity: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    returnedQuantityBefore: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    returnQuantity: PaidOrderItemReturnRequestFields.quantity,
    returnedQuantityAfter: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    unitSalePriceFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    paymentMethod: Type.Union([PaidSaleReturnPaymentMethodSchema, Type.Null()]),
    refundExecutionMode: Type.Union([PaidSaleReturnRefundExecutionModeSchema, Type.Null()]),
    paymentMethodLabel: Type.String({ minLength: 2, maxLength: 80 }),
    paymentSourceCount: Type.Optional(Type.Integer({ minimum: 1, maximum: 32 })),
    refundAllocations: Type.Optional(PaidSaleReturnOriginalPaymentsSchema),
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    inventoryQuantityBefore: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    inventoryQuantityAfter: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    reason: MemberBalanceRefundReasonSchema,
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    requirement: ApprovalRequirementSchema,
    approverLabel: Type.String({ minLength: 2, maxLength: 120 }),
    canSubmit: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type PaidOrderItemReturnApprovalPreviewReadModel = Static<
  typeof PaidOrderItemReturnApprovalPreviewReadModelSchema
>;

export const CreatePaidOrderItemReturnApprovalCommandSchema = Type.Object(
  {
    ...PaidOrderItemReturnRequestFields,
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreatePaidOrderItemReturnApprovalCommand = Static<
  typeof CreatePaidOrderItemReturnApprovalCommandSchema
>;

const InventoryOperationApprovalRequestFields = {
  skuId: Type.String({ minLength: 1, maxLength: 64, pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$" }),
  operationType: InventoryOperationTypeSchema,
  quantity: InventoryRequestedQuantitySchema,
  unitCostFen: Type.Union([InventoryMoneyFenSchema, Type.Null()]),
  referenceNumber: InventoryReferenceSchema,
  reason: InventoryReasonSchema,
};

export const PreviewInventoryOperationApprovalRequestSchema = Type.Object(
  InventoryOperationApprovalRequestFields,
  { additionalProperties: false },
);
export type PreviewInventoryOperationApprovalRequest = Static<
  typeof PreviewInventoryOperationApprovalRequestSchema
>;

export const InventoryOperationApprovalPreviewReadModelSchema = Type.Object(
  {
    kind: Type.Literal("INVENTORY_OPERATION"),
    skuId: InventoryOperationApprovalRequestFields.skuId,
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    operationType: InventoryOperationTypeSchema,
    requestedQuantity: InventoryRequestedQuantitySchema,
    quantityBefore: InventoryFactQuantitySchema,
    quantityDelta: InventoryFactQuantitySchema,
    quantityAfter: InventoryFactQuantitySchema,
    unitCostFen: Type.Union([InventoryMoneyFenSchema, Type.Null()]),
    referenceNumber: InventoryReferenceSchema,
    reason: InventoryReasonSchema,
    allowNegativeStock: Type.Boolean(),
    sourceReferenceSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    requirement: ApprovalRequirementSchema,
    approverLabel: Type.String({ minLength: 2, maxLength: 120 }),
    canSubmit: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
    generatedAt: ApprovalDateTimeSchema,
    countCapture: Type.Optional(InventoryCountCaptureSchema),
    lossCapture: Type.Optional(InventoryLossCaptureSchema),
  },
  { additionalProperties: false },
);
export type InventoryOperationApprovalPreviewReadModel = Static<
  typeof InventoryOperationApprovalPreviewReadModelSchema
>;

export const CreateInventoryOperationApprovalCommandSchema = Type.Object(
  {
    ...InventoryOperationApprovalRequestFields,
    sourceReferenceSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateInventoryOperationApprovalCommand = Static<
  typeof CreateInventoryOperationApprovalCommandSchema
>;

export const OrderCashRefundApprovalSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("ORDER_CASH_REFUND"),
    snapshotVersion: Type.Union([Type.Literal(1), Type.Literal(2)]),
    orderId: ApprovalIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    tableNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
    orderStatus: Type.Union([Type.Literal("SETTLED"), Type.Literal("REFUNDED")]),
    orderPaidFen: ApprovalMoneyFenSchema,
    orderAlreadyRefundedFen: ApprovalMoneyFenSchema,
    paymentId: ApprovalIdSchema,
    paymentMethod: Type.Literal("CASH"),
    paymentAmountFen: ApprovalMoneyFenSchema,
    paymentAlreadyRefundedFen: ApprovalMoneyFenSchema,
    paymentAvailableFen: ApprovalMoneyFenSchema,
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    reason: ApprovalReasonSchema,
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    commandSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    capturedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type OrderCashRefundApprovalSnapshot = Static<typeof OrderCashRefundApprovalSnapshotSchema>;

export const MemberBalanceRefundApprovalSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("MEMBER_BALANCE_REFUND"),
    snapshotVersion: Type.Union([Type.Literal(1), Type.Literal(2)]),
    memberId: ApprovalIdSchema,
    memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    transactionOccurredAt: ApprovalDateTimeSchema,
    originalWalletAmountFen: ApprovalMoneyFenSchema,
    alreadyRefundedFen: ApprovalMoneyFenSchema,
    refundableRemainingFen: ApprovalMoneyFenSchema,
    amountFen: MemberBalanceRefundAmountFenSchema,
    reason: MemberBalanceRefundReasonSchema,
    refundAllocations: Type.Optional(MemberRefundWalletAllocationsSchema),
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    sourceReferenceSha256: ApprovalSha256Schema,
    commandSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    capturedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type MemberBalanceRefundApprovalSnapshot = Static<
  typeof MemberBalanceRefundApprovalSnapshotSchema
>;

export const PaidOrderItemReturnApprovalSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("ORDER_ITEM_PAID_RETURN"),
    snapshotVersion: Type.Union([
      Type.Literal(1),
      Type.Literal(2),
      Type.Literal(3),
      Type.Literal(4),
    ]),
    itemKind: Type.Optional(Type.Union([Type.Literal("PRODUCT"), Type.Literal("PACKAGE")])),
    lineNumber: Type.Optional(Type.String({ minLength: 1, maxLength: 80 })),
    componentNames: Type.Optional(
      Type.Array(Type.String({ minLength: 1, maxLength: 80 }), {
        minItems: 1,
        maxItems: 30,
      }),
    ),
    orderId: ApprovalIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    orderStatus: Type.Union([Type.Literal("SETTLED"), Type.Literal("REFUNDED")]),
    itemId: ApprovalIdSchema,
    skuId: ApprovalIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    soldQuantity: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    returnedQuantityBefore: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    returnQuantity: PaidOrderItemReturnRequestFields.quantity,
    returnedQuantityAfter: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    unitSalePriceFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    paymentMethod: Type.Union([PaidSaleReturnPaymentMethodSchema, Type.Null()]),
    refundExecutionMode: Type.Union([PaidSaleReturnRefundExecutionModeSchema, Type.Null()]),
    paymentMethodLabel: Type.String({ minLength: 2, maxLength: 80 }),
    paymentSourceCount: Type.Optional(Type.Integer({ minimum: 1, maximum: 32 })),
    refundAllocations: Type.Optional(PaidSaleReturnOriginalPaymentsSchema),
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    inventoryQuantityBefore: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    inventoryQuantityAfter: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    reason: MemberBalanceRefundReasonSchema,
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    commandSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    capturedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type PaidOrderItemReturnApprovalSnapshot = Static<
  typeof PaidOrderItemReturnApprovalSnapshotSchema
>;

export const InventoryOperationApprovalSnapshotSchema = Type.Object(
  {
    kind: Type.Literal("INVENTORY_OPERATION"),
    snapshotVersion: Type.Union([Type.Literal(1), Type.Literal(2)]),
    skuId: InventoryOperationApprovalRequestFields.skuId,
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    operationType: InventoryOperationTypeSchema,
    requestedQuantity: InventoryRequestedQuantitySchema,
    quantityBefore: InventoryFactQuantitySchema,
    quantityDelta: InventoryFactQuantitySchema,
    quantityAfter: InventoryFactQuantitySchema,
    unitCostFen: Type.Union([InventoryMoneyFenSchema, Type.Null()]),
    referenceNumber: InventoryReferenceSchema,
    reason: InventoryReasonSchema,
    allowNegativeStock: Type.Boolean(),
    originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
    consequences: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    nextSteps: Type.Array(Type.String({ minLength: 2, maxLength: 300 }), {
      minItems: 1,
      maxItems: 8,
      uniqueItems: true,
    }),
    sourceReferenceSha256: ApprovalSha256Schema,
    commandSha256: ApprovalSha256Schema,
    businessFactSha256: ApprovalSha256Schema,
    capturedAt: ApprovalDateTimeSchema,
    countCapture: Type.Optional(InventoryCountCaptureSchema),
    lossCapture: Type.Optional(InventoryLossCaptureSchema),
  },
  { additionalProperties: false },
);
export type InventoryOperationApprovalSnapshot = Static<
  typeof InventoryOperationApprovalSnapshotSchema
>;

export const MemberBalanceRefundApprovalExecutionBindingSchema = Type.Object(
  {
    bindingVersion: Type.Literal(1),
    tenantId: ApprovalIdSchema,
    storeId: ApprovalIdSchema,
    memberId: ApprovalIdSchema,
    orderId: ApprovalIdSchema,
    originalTransactionId: ApprovalIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 80 }),
    sourceOperationTrace: BusinessOperationTraceV1Schema,
  },
  { additionalProperties: false },
);
export type MemberBalanceRefundApprovalExecutionBinding = Static<
  typeof MemberBalanceRefundApprovalExecutionBindingSchema
>;

export const MemberBalanceRefundApprovalPreparationSchema = Type.Object(
  {
    preview: MemberBalanceRefundApprovalPreviewReadModelSchema,
    executionBinding: MemberBalanceRefundApprovalExecutionBindingSchema,
    businessFactSha256: ApprovalSha256Schema,
  },
  { additionalProperties: false },
);
export type MemberBalanceRefundApprovalPreparation = Static<
  typeof MemberBalanceRefundApprovalPreparationSchema
>;

export const PaidOrderItemReturnApprovalPreparationSchema = Type.Object(
  {
    preview: PaidOrderItemReturnApprovalPreviewReadModelSchema,
    executionBinding: PaidOrderItemSaleReturnApprovalBindingSchema,
    businessFactSha256: ApprovalSha256Schema,
  },
  { additionalProperties: false },
);
export type PaidOrderItemReturnApprovalPreparation = Static<
  typeof PaidOrderItemReturnApprovalPreparationSchema
>;

export const InventoryOperationApprovalExecutionBindingSchema = Type.Object(
  {
    bindingVersion: Type.Literal(1),
    tenantId: ApprovalIdSchema,
    storeId: ApprovalIdSchema,
    skuId: InventoryOperationApprovalRequestFields.skuId,
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    quantityBefore: InventoryFactQuantitySchema,
    countCapture: Type.Optional(InventoryCountCaptureSchema),
    lossCapture: Type.Optional(InventoryLossCaptureSchema),
  },
  { additionalProperties: false },
);
export type InventoryOperationApprovalExecutionBinding = Static<
  typeof InventoryOperationApprovalExecutionBindingSchema
>;

export const InventoryOperationApprovalPreparationSchema = Type.Object(
  {
    preview: InventoryOperationApprovalPreviewReadModelSchema,
    executionBinding: InventoryOperationApprovalExecutionBindingSchema,
    businessFactSha256: ApprovalSha256Schema,
  },
  { additionalProperties: false },
);
export type InventoryOperationApprovalPreparation = Static<
  typeof InventoryOperationApprovalPreparationSchema
>;

export const ApprovalOperationSnapshotSchema = Type.Union([
  OrderCashRefundApprovalSnapshotSchema,
  MemberBalanceRefundApprovalSnapshotSchema,
  PaidOrderItemReturnApprovalSnapshotSchema,
  InventoryOperationApprovalSnapshotSchema,
  PurchaseOrderApprovalSnapshotSchema,
  GroupBuyDailyReconciliationApprovalSnapshotSchema,
  GroupBuyReconciliationResolutionApprovalSnapshotSchema,
  CashShiftDifferenceApprovalSnapshotSchema,
  ManualReviewResolutionApprovalSnapshotSchema,
]);
export type ApprovalOperationSnapshot = Static<typeof ApprovalOperationSnapshotSchema>;

const ApprovalPolicySnapshotCommonFields = {
  requirement: ApprovalRequirementSchema,
  effect: Type.Union([
    Type.Literal("MANAGER_OR_OWNER_APPROVAL"),
    Type.Literal("MANAGER_THEN_OWNER_APPROVAL"),
    Type.Literal("OWNER_APPROVAL"),
  ]),
  policyVersion: Type.Integer({ minimum: 1 }),
  policyContentSha256: ApprovalSha256Schema,
  matchedRuleId: ApprovalIdSchema,
  authorizationRequestId: ApprovalIdSchema,
};

export const ApprovalPolicySnapshotSchema = Type.Union([
  Type.Object(
    {
      ...ApprovalPolicySnapshotCommonFields,
      resource: Type.Literal("PAYMENT"),
      action: Type.Literal("REQUEST_REFUND"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ApprovalPolicySnapshotCommonFields,
      resource: Type.Literal("INVENTORY"),
      action: Type.Literal("ADJUST"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ApprovalPolicySnapshotCommonFields,
      resource: Type.Literal("PURCHASE_ORDER"),
      action: Type.Literal("SUBMIT_APPROVAL"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ApprovalPolicySnapshotCommonFields,
      resource: Type.Literal("ORDER"),
      action: Type.Literal("RECONCILE_GROUP_BUY"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ApprovalPolicySnapshotCommonFields,
      resource: Type.Literal("SHIFT"),
      action: Type.Literal("HANDOVER"),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      ...ApprovalPolicySnapshotCommonFields,
      resource: Type.Literal("MANUAL_REVIEW"),
      action: Type.Literal("RESOLVE"),
    },
    { additionalProperties: false },
  ),
]);
export type ApprovalPolicySnapshot = Static<typeof ApprovalPolicySnapshotSchema>;

export const ApprovalStepSchema = Type.Object(
  {
    stepNumber: Type.Integer({ minimum: 1, maximum: 2 }),
    requiredRole: Type.Union([
      Type.Literal("MANAGER_OR_OWNER"),
      Type.Literal("MANAGER"),
      Type.Literal("OWNER"),
    ]),
    status: Type.Union([
      Type.Literal("WAITING"),
      Type.Literal("AVAILABLE"),
      Type.Literal("APPROVED"),
      Type.Literal("REJECTED"),
      Type.Literal("CANCELLED"),
    ]),
    decidedBy: Type.Union([ApprovalActorSummarySchema, Type.Null()]),
    decidedAt: Type.Union([ApprovalDateTimeSchema, Type.Null()]),
    reason: Type.Union([ApprovalReasonSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type ApprovalStep = Static<typeof ApprovalStepSchema>;

export const ApprovalTimelineEventSchema = Type.Object(
  {
    eventId: ApprovalIdSchema,
    type: Type.Union([
      Type.Literal("REQUESTED"),
      Type.Literal("STEP_APPROVED"),
      Type.Literal("REJECTED"),
      Type.Literal("CANCELLED"),
      Type.Literal("OWNER_REMINDER_ESCALATED"),
      Type.Literal("EXECUTION_STARTED"),
      Type.Literal("EXECUTION_FAILED"),
      Type.Literal("EXECUTION_OUTCOME_UNKNOWN"),
      Type.Literal("EXECUTION_PRECONDITION_CHANGED"),
      Type.Literal("EXECUTION_INTEGRITY_REVIEW_REQUIRED"),
      Type.Literal("EXECUTION_SUCCEEDED"),
    ]),
    actor: ApprovalActorSummarySchema,
    summary: Type.String({ minLength: 2, maxLength: 300 }),
    reason: Type.Union([ApprovalReasonSchema, Type.Null()]),
    occurredAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ApprovalTimelineEvent = Static<typeof ApprovalTimelineEventSchema>;

export const ApprovalExecutionReadModelSchema = Type.Union([
  Type.Object(
    {
      status: Type.Union([Type.Literal("AWAITING_APPROVAL"), Type.Literal("NOT_APPLICABLE")]),
      attempts: Type.Literal(0),
      lastAttemptAt: Type.Null(),
      lastErrorMessage: Type.Null(),
      completedAt: Type.Null(),
      resultReference: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      status: Type.Literal("READY"),
      attempts: Type.Literal(0),
      lastAttemptAt: Type.Null(),
      lastErrorMessage: Type.Null(),
      completedAt: Type.Null(),
      resultReference: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      status: Type.Literal("PROCESSING"),
      attempts: Type.Integer({ minimum: 1 }),
      lastAttemptAt: ApprovalDateTimeSchema,
      lastErrorMessage: Type.Null(),
      completedAt: Type.Null(),
      resultReference: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      status: Type.Union([
        Type.Literal("OUTCOME_UNKNOWN"),
        Type.Literal("FAILED_RETRYABLE"),
        Type.Literal("BLOCKED_PRECONDITION_CHANGED"),
        Type.Literal("BLOCKED_INTEGRITY_REVIEW"),
        Type.Literal("FAILED_TERMINAL"),
      ]),
      attempts: Type.Integer({ minimum: 1 }),
      lastAttemptAt: ApprovalDateTimeSchema,
      lastErrorMessage: Type.String({ minLength: 2, maxLength: 300 }),
      completedAt: Type.Null(),
      resultReference: Type.Null(),
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      status: Type.Literal("SUCCEEDED"),
      attempts: Type.Integer({ minimum: 1 }),
      lastAttemptAt: ApprovalDateTimeSchema,
      lastErrorMessage: Type.Null(),
      completedAt: ApprovalDateTimeSchema,
      resultReference: ApprovalIdSchema,
    },
    { additionalProperties: false },
  ),
]);
export type ApprovalExecutionReadModel = Static<typeof ApprovalExecutionReadModelSchema>;

export const ApprovalViewerActionsSchema = Type.Object(
  {
    canApprove: Type.Boolean(),
    canReject: Type.Boolean(),
    canCancel: Type.Boolean(),
    canRetryExecution: Type.Boolean(),
    unavailableReason: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type ApprovalViewerActions = Static<typeof ApprovalViewerActionsSchema>;

export const ApprovalRequestReadModelSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    version: Type.Integer({ minimum: 1 }),
    status: ApprovalRequestStatusSchema,
    title: Type.String({ minLength: 2, maxLength: 120 }),
    applicant: ApprovalActorSummarySchema,
    requestedAt: ApprovalDateTimeSchema,
    updatedAt: ApprovalDateTimeSchema,
    snapshotSha256: ApprovalSha256Schema,
    snapshot: ApprovalOperationSnapshotSchema,
    policy: ApprovalPolicySnapshotSchema,
    steps: Type.Array(ApprovalStepSchema, { minItems: 1, maxItems: 2 }),
    execution: ApprovalExecutionReadModelSchema,
    timeline: Type.Array(ApprovalTimelineEventSchema, { minItems: 1, maxItems: 200 }),
    actions: ApprovalViewerActionsSchema,
  },
  { additionalProperties: false },
);
export type ApprovalRequestReadModel = Static<typeof ApprovalRequestReadModelSchema>;

const ApprovalRequestListItemCommonFields = {
  approvalId: ApprovalIdSchema,
  version: Type.Integer({ minimum: 1 }),
  status: ApprovalRequestStatusSchema,
  title: Type.String({ minLength: 2, maxLength: 120 }),
  originalDocumentLabel: Type.String({ minLength: 2, maxLength: 120 }),
  applicant: ApprovalActorSummarySchema,
  requestedAt: ApprovalDateTimeSchema,
  currentStepLabel: Type.String({ minLength: 2, maxLength: 120 }),
  executionStatus: Type.Union([
    Type.Literal("AWAITING_APPROVAL"),
    Type.Literal("NOT_APPLICABLE"),
    Type.Literal("READY"),
    Type.Literal("PROCESSING"),
    Type.Literal("OUTCOME_UNKNOWN"),
    Type.Literal("FAILED_RETRYABLE"),
    Type.Literal("BLOCKED_PRECONDITION_CHANGED"),
    Type.Literal("BLOCKED_INTEGRITY_REVIEW"),
    Type.Literal("FAILED_TERMINAL"),
    Type.Literal("SUCCEEDED"),
  ]),
  actions: ApprovalViewerActionsSchema,
};

export const ApprovalRequestListItemReadModelSchema = Type.Object(
  {
    ...ApprovalRequestListItemCommonFields,
    summary: Type.Union([
      Type.Object(
        {
          kind: Type.Union([
            Type.Literal("ORDER_CASH_REFUND"),
            Type.Literal("MEMBER_BALANCE_REFUND"),
            Type.Literal("ORDER_ITEM_PAID_RETURN"),
          ]),
          amountFen: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal("INVENTORY_OPERATION"),
          operationType: InventoryOperationTypeSchema,
          quantityDelta: InventoryFactQuantitySchema,
          quantityAfter: InventoryFactQuantitySchema,
          unit: Type.String({ minLength: 1, maxLength: 16 }),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal("PURCHASE_ORDER_APPROVAL"),
          supplierName: Type.String({ minLength: 1, maxLength: 80 }),
          lineCount: Type.Integer({ minimum: 1, maximum: 200 }),
          subtotalFen: ApprovalMoneyFenSchema,
          taxFen: ApprovalMoneyFenSchema,
          totalFen: ApprovalMoneyFenSchema,
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal("GROUP_BUY_DAILY_RECONCILIATION"),
          provider: Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]),
          businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
          fulfillmentCount: Type.Integer({ minimum: 0, maximum: 500 }),
          confirmedCount: Type.Integer({ minimum: 0, maximum: 500 }),
          exceptionCount: Type.Integer({ minimum: 0, maximum: 1_000 }),
          actualSettlementFen: ApprovalMoneyFenSchema,
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal("GROUP_BUY_RECONCILIATION_RESOLUTION"),
          provider: Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]),
          businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
          exceptionCount: Type.Integer({ minimum: 1, maximum: 1_000 }),
          platformReceivableFen: ApprovalMoneyFenSchema,
          settlementAdjustmentFen: Type.Integer({
            minimum: -Number.MAX_SAFE_INTEGER,
            maximum: Number.MAX_SAFE_INTEGER,
          }),
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal("CASH_SHIFT_DIFFERENCE"),
          businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
          expectedCashFen: Type.Integer({
            minimum: -Number.MAX_SAFE_INTEGER,
            maximum: Number.MAX_SAFE_INTEGER,
          }),
          countedCashFen: ApprovalMoneyFenSchema,
          cashDifferenceFen: Type.Integer({
            minimum: -Number.MAX_SAFE_INTEGER,
            maximum: Number.MAX_SAFE_INTEGER,
          }),
          tier: CashShiftDifferenceTierSchema,
        },
        { additionalProperties: false },
      ),
      Type.Object(
        {
          kind: Type.Literal("MANUAL_REVIEW_RESOLUTION"),
          category: ManualReviewResolutionApprovalSnapshotSchema.properties.category,
          resolutionCode: ManualReviewResolutionApprovalSnapshotSchema.properties.resolutionCode,
          amountFen: ManualReviewResolutionApprovalSnapshotSchema.properties.amountFen,
        },
        { additionalProperties: false },
      ),
    ]),
  },
  { additionalProperties: false },
);
export type ApprovalRequestListItemReadModel = Static<
  typeof ApprovalRequestListItemReadModelSchema
>;

export const ApprovalRequestListReadModelSchema = Type.Object(
  {
    items: Type.Array(ApprovalRequestListItemReadModelSchema, { maxItems: 100 }),
    pendingCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    generatedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ApprovalRequestListReadModel = Static<typeof ApprovalRequestListReadModelSchema>;

export const ApprovalListViewSchema = Type.Union([
  Type.Literal("TO_DECIDE"),
  Type.Literal("MINE"),
  Type.Literal("HISTORY"),
]);
export type ApprovalListView = Static<typeof ApprovalListViewSchema>;

export const ApprovalSummaryReadModelSchema = Type.Object(
  {
    toDecideCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    myPendingCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    historyCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    executionAttentionCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    generatedAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type ApprovalSummaryReadModel = Static<typeof ApprovalSummaryReadModelSchema>;

export const DecideApprovalCommandSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    expectedCaseVersion: Type.Integer({ minimum: 1 }),
    expectedStepNumber: Type.Integer({ minimum: 1, maximum: 2 }),
    decision: Type.Union([Type.Literal("APPROVE"), Type.Literal("REJECT")]),
    reason: ApprovalReasonSchema,
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type DecideApprovalCommand = Static<typeof DecideApprovalCommandSchema>;

export const CancelApprovalCommandSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    expectedCaseVersion: Type.Integer({ minimum: 1 }),
    reason: ApprovalReasonSchema,
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CancelApprovalCommand = Static<typeof CancelApprovalCommandSchema>;

export const RetryApprovalExecutionCommandSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    expectedCaseVersion: Type.Integer({ minimum: 1 }),
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RetryApprovalExecutionCommand = Static<typeof RetryApprovalExecutionCommandSchema>;

export const ExecutePaidOrderItemReturnCommandSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    expectedCaseVersion: Type.Integer({ minimum: 1 }),
    externalRefundReference: Type.Union([
      Type.String({ minLength: 2, maxLength: 128, pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{1,127}$" }),
      Type.Null(),
    ]),
    externalRefundReferences: Type.Optional(
      Type.Array(
        Type.Object(
          {
            paymentId: ApprovalIdSchema,
            externalRefundReference: Type.String({
              minLength: 2,
              maxLength: 128,
              pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{1,127}$",
            }),
          },
          { additionalProperties: false },
        ),
        { maxItems: 32 },
      ),
    ),
    idempotencyKey: ApprovalIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ExecutePaidOrderItemReturnCommand = Static<
  typeof ExecutePaidOrderItemReturnCommandSchema
>;

export const CreateApprovalRequestInternalCommandSchema = Type.Object(
  {
    approvalId: ApprovalIdSchema,
    tenantId: ApprovalIdSchema,
    storeId: ApprovalIdSchema,
    applicantPersonId: ApprovalIdSchema,
    applicantAccountId: ApprovalIdSchema,
    applicantEmploymentId: ApprovalIdSchema,
    applicantRoles: Type.Array(
      Type.Union([
        Type.Literal("OWNER"),
        Type.Literal("MANAGER"),
        Type.Literal("CASHIER"),
        Type.Literal("WAITER"),
        Type.Literal("COACH"),
      ]),
      { minItems: 1, maxItems: 5, uniqueItems: true },
    ),
    applicantIdentityVersion: Type.Integer({ minimum: 1 }),
    applicantProjectionVersion: Type.Integer({ minimum: 1 }),
    applicantAuthorizationFactHash: ApprovalSha256Schema,
    applicant: ApprovalActorSummarySchema,
    idempotencyKey: ApprovalIdempotencyKeySchema,
    snapshotSha256: ApprovalSha256Schema,
    snapshot: ApprovalOperationSnapshotSchema,
    policy: ApprovalPolicySnapshotSchema,
    occurredAt: ApprovalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CreateApprovalRequestInternalCommand = Static<
  typeof CreateApprovalRequestInternalCommandSchema
>;

function isOrderCashRefundSnapshotConsistent(snapshot: OrderCashRefundApprovalSnapshot): boolean {
  const orderRemainingFen = snapshot.orderPaidFen - snapshot.orderAlreadyRefundedFen;
  return (
    isOrderNumberForOrderId(snapshot.orderNumber, snapshot.orderId, snapshot.businessDate) &&
    isCanonicalApprovalTimestamp(snapshot.capturedAt) &&
    Number.isSafeInteger(orderRemainingFen) &&
    orderRemainingFen >= snapshot.amountFen &&
    snapshot.paymentAlreadyRefundedFen <= snapshot.paymentAmountFen &&
    snapshot.paymentAvailableFen ===
      snapshot.paymentAmountFen - snapshot.paymentAlreadyRefundedFen &&
    snapshot.amountFen <= snapshot.paymentAvailableFen
  );
}

function isOrderNumberForBusinessDate(orderNumber: string, businessDate: string): boolean {
  return (
    isOrderNumber(orderNumber) && orderNumber.slice(3, 11) === businessDate.replaceAll("-", "")
  );
}

function isMemberBalanceRefundSnapshotConsistent(
  snapshot: MemberBalanceRefundApprovalSnapshot,
): boolean {
  const maximumRefundableFen = snapshot.originalWalletAmountFen - snapshot.alreadyRefundedFen;
  const allocationShapeIsValid =
    snapshot.snapshotVersion === 1
      ? snapshot.refundAllocations === undefined
      : snapshot.refundAllocations !== undefined &&
        memberRefundAllocationsAreConsistent(snapshot.refundAllocations, snapshot.amountFen);
  return (
    isOrderNumberForBusinessDate(snapshot.orderNumber, snapshot.businessDate) &&
    isCanonicalApprovalTimestamp(snapshot.transactionOccurredAt) &&
    isCanonicalApprovalTimestamp(snapshot.capturedAt) &&
    Number.isSafeInteger(maximumRefundableFen) &&
    maximumRefundableFen >= 0 &&
    snapshot.refundableRemainingFen <= maximumRefundableFen &&
    snapshot.amountFen <= snapshot.refundableRemainingFen &&
    allocationShapeIsValid
  );
}

export function isMemberBalanceRefundApprovalSnapshot(
  value: unknown,
): value is MemberBalanceRefundApprovalSnapshot {
  return (
    Value.Check(MemberBalanceRefundApprovalSnapshotSchema, value) &&
    isMemberBalanceRefundSnapshotConsistent(value)
  );
}

function memberRefundAllocationsAreConsistent(
  allocations: readonly MemberRefundWalletAllocation[],
  amountFen: number,
): boolean {
  return (
    allocations.length > 0 &&
    new Set(allocations.map((allocation) => allocation.walletDefinitionId)).size ===
      allocations.length &&
    allocations.reduce((total, allocation) => total + allocation.refundAmountFen, 0) === amountFen
  );
}

function paidOrderItemReturnAmountsAreConsistent(
  value: {
    itemKind?: "PRODUCT" | "PACKAGE";
    soldQuantity: number;
    returnedQuantityBefore: number;
    returnQuantity: number;
    returnedQuantityAfter: number;
    unitSalePriceFen: number;
    amountFen: number;
    inventoryQuantityBefore: number | null;
    inventoryQuantityAfter: number | null;
    physicalDisposition: PaidOrderItemReturnApprovalSnapshot["physicalDisposition"];
    inventoryEffect: PaidOrderItemReturnApprovalSnapshot["inventoryEffect"];
  },
  legacyUnsaleableInventorySnapshot = false,
): boolean {
  const amountFen = value.unitSalePriceFen * value.returnQuantity;
  if (value.itemKind === "PACKAGE") {
    return (
      value.soldQuantity === 1 &&
      value.returnedQuantityBefore === 0 &&
      value.returnQuantity === 1 &&
      value.returnedQuantityAfter === 1 &&
      value.amountFen === amountFen &&
      value.inventoryEffect ===
        saleReturnInventoryEffectForDisposition(value.physicalDisposition) &&
      value.inventoryQuantityBefore === null &&
      value.inventoryQuantityAfter === null
    );
  }
  return (
    Number.isSafeInteger(amountFen) &&
    value.returnedQuantityAfter === value.returnedQuantityBefore + value.returnQuantity &&
    value.returnedQuantityAfter <= value.soldQuantity &&
    value.amountFen === amountFen &&
    value.inventoryEffect === saleReturnInventoryEffectForDisposition(value.physicalDisposition) &&
    (value.physicalDisposition === "SALEABLE_RETURNED"
      ? value.inventoryQuantityBefore !== null &&
        value.inventoryQuantityAfter === value.inventoryQuantityBefore + value.returnQuantity
      : legacyUnsaleableInventorySnapshot
        ? value.inventoryQuantityBefore !== null &&
          value.inventoryQuantityAfter === value.inventoryQuantityBefore
        : value.inventoryQuantityBefore === null && value.inventoryQuantityAfter === null)
  );
}

function isPaidOrderItemReturnSnapshotConsistent(
  snapshot: PaidOrderItemReturnApprovalSnapshot,
): boolean {
  const allocations = snapshot.refundAllocations;
  const allocationShapeIsValid = allocations
    ? (snapshot.snapshotVersion === 3 || snapshot.snapshotVersion === 4) &&
      snapshot.paymentSourceCount !== undefined &&
      allocations.length <= snapshot.paymentSourceCount &&
      snapshot.paymentMethod === null &&
      snapshot.refundExecutionMode === null &&
      allocations.reduce((sum, payment) => sum + payment.refundAmountFen, 0) === snapshot.amountFen
    : snapshot.snapshotVersion !== 3 &&
      snapshot.snapshotVersion !== 4 &&
      snapshot.paymentSourceCount === undefined &&
      snapshot.paymentMethod !== null &&
      snapshot.refundExecutionMode !== null;
  return (
    isOrderNumberForOrderId(snapshot.orderNumber, snapshot.orderId, snapshot.businessDate) &&
    isCanonicalApprovalTimestamp(snapshot.capturedAt) &&
    allocationShapeIsValid &&
    (snapshot.itemKind === "PACKAGE"
      ? snapshot.snapshotVersion === 4 &&
        snapshot.lineNumber !== undefined &&
        snapshot.componentNames !== undefined
      : snapshot.snapshotVersion !== 4) &&
    paidOrderItemReturnAmountsAreConsistent(snapshot, snapshot.snapshotVersion === 1)
  );
}

function inventoryOperationQuantityIsValid(
  operationType: InventoryOperationApprovalSnapshot["operationType"],
  quantity: number,
): boolean {
  switch (operationType) {
    case "PURCHASE_IN":
    case "OTHER_IN":
    case "RETURN_OUT":
    case "LOSS_OUT":
    case "INTERNAL_USE_OUT":
      return quantity >= 1;
    case "COUNT_SET":
      return quantity >= 0;
    case "MANUAL_ADJUSTMENT":
      return quantity !== 0;
  }
}

function inventoryOperationCalculation(
  operationType: InventoryOperationApprovalSnapshot["operationType"],
  requestedQuantity: number,
  quantityBefore: number,
): { quantityDelta: number; quantityAfter: number } {
  switch (operationType) {
    case "PURCHASE_IN":
    case "OTHER_IN":
    case "MANUAL_ADJUSTMENT":
      return {
        quantityDelta: requestedQuantity,
        quantityAfter: quantityBefore + requestedQuantity,
      };
    case "RETURN_OUT":
    case "LOSS_OUT":
    case "INTERNAL_USE_OUT":
      return {
        quantityDelta: -requestedQuantity,
        quantityAfter: quantityBefore - requestedQuantity,
      };
    case "COUNT_SET":
      return {
        quantityDelta: requestedQuantity - quantityBefore,
        quantityAfter: requestedQuantity,
      };
  }
}

function inventoryOperationCalculationIsConsistent(value: {
  operationType: InventoryOperationApprovalSnapshot["operationType"];
  requestedQuantity: number;
  quantityBefore: number;
  quantityDelta: number;
  quantityAfter: number;
}): boolean {
  const calculated = inventoryOperationCalculation(
    value.operationType,
    value.requestedQuantity,
    value.quantityBefore,
  );
  return (
    Number.isSafeInteger(calculated.quantityDelta) &&
    Number.isSafeInteger(calculated.quantityAfter) &&
    calculated.quantityDelta === value.quantityDelta &&
    calculated.quantityAfter === value.quantityAfter
  );
}

function isInventoryOperationSnapshotConsistent(
  snapshot: InventoryOperationApprovalSnapshot,
): boolean {
  const captureIsConsistent = inventoryOperationCaptureMatchesOperation(
    snapshot.operationType,
    snapshot.requestedQuantity,
    snapshot.quantityBefore,
    snapshot.quantityAfter,
    snapshot.unitCostFen,
    snapshot.snapshotVersion,
    snapshot.countCapture,
    snapshot.lossCapture,
  );
  return (
    isCanonicalApprovalTimestamp(snapshot.capturedAt) &&
    inventoryOperationQuantityIsValid(snapshot.operationType, snapshot.requestedQuantity) &&
    inventoryOperationCalculationIsConsistent(snapshot) &&
    snapshot.quantityDelta !== 0 &&
    (snapshot.allowNegativeStock || snapshot.quantityAfter >= 0) &&
    captureIsConsistent
  );
}

function inventoryOperationCaptureMatchesOperation(
  operationType: InventoryOperationApprovalSnapshot["operationType"],
  requestedQuantity: number,
  quantityBefore: number,
  quantityAfter: number,
  unitCostFen: number | null,
  snapshotVersion: 1 | 2,
  countCapture: InventoryCountCapture | undefined,
  lossCapture: InventoryLossCapture | undefined,
): boolean {
  if (operationType === "COUNT_SET") {
    if (lossCapture !== undefined) return false;
    if (snapshotVersion === 1) return countCapture === undefined;
    return (
      countCapture !== undefined &&
      isInventoryCountCapture(countCapture) &&
      countCapture.countedQuantity === requestedQuantity &&
      countCapture.theoreticalQuantity === quantityBefore &&
      countCapture.surplusUnitCostFen === unitCostFen
    );
  }
  if (operationType === "LOSS_OUT") {
    if (snapshotVersion === 1) {
      return countCapture === undefined && lossCapture === undefined;
    }
    return (
      countCapture === undefined &&
      lossCapture !== undefined &&
      isInventoryLossCapture(lossCapture) &&
      lossCapture.quantity === requestedQuantity &&
      lossCapture.quantityBefore === quantityBefore &&
      lossCapture.quantityAfter === quantityAfter &&
      unitCostFen === null
    );
  }
  return snapshotVersion === 1 && countCapture === undefined && lossCapture === undefined;
}

function isPurchaseOrderSnapshotConsistent(snapshot: PurchaseOrderApprovalSnapshot): boolean {
  return isPurchaseOrderApprovalSnapshot(snapshot);
}

function isApprovalOperationSnapshotConsistent(snapshot: ApprovalOperationSnapshot): boolean {
  switch (snapshot.kind) {
    case "ORDER_CASH_REFUND":
      return isOrderCashRefundSnapshotConsistent(snapshot);
    case "MEMBER_BALANCE_REFUND":
      return isMemberBalanceRefundSnapshotConsistent(snapshot);
    case "ORDER_ITEM_PAID_RETURN":
      return isPaidOrderItemReturnSnapshotConsistent(snapshot);
    case "INVENTORY_OPERATION":
      return isInventoryOperationSnapshotConsistent(snapshot);
    case "PURCHASE_ORDER_APPROVAL":
      return isPurchaseOrderSnapshotConsistent(snapshot);
    case "GROUP_BUY_DAILY_RECONCILIATION":
      return isGroupBuyDailyReconciliationApprovalSnapshot(snapshot);
    case "GROUP_BUY_RECONCILIATION_RESOLUTION":
      return isGroupBuyReconciliationResolutionApprovalSnapshot(snapshot);
    case "CASH_SHIFT_DIFFERENCE":
      return isCashShiftDifferenceApprovalSnapshot(snapshot);
    case "MANUAL_REVIEW_RESOLUTION":
      return isManualReviewResolutionApprovalSnapshot(snapshot);
  }
}

function approvalPolicyAndApplicantMatchOperation(value: ApprovalRequestReadModel): boolean {
  if (value.snapshot.kind === "PURCHASE_ORDER_APPROVAL") {
    return (
      value.policy.resource === "PURCHASE_ORDER" &&
      value.policy.action === "SUBMIT_APPROVAL" &&
      (value.applicant.role === "OWNER" || value.applicant.role === "MANAGER")
    );
  }
  if (value.snapshot.kind === "INVENTORY_OPERATION") {
    return (
      value.policy.resource === "INVENTORY" &&
      value.policy.action === "ADJUST" &&
      (value.applicant.role === "OWNER" || value.applicant.role === "MANAGER")
    );
  }
  if (
    value.snapshot.kind === "GROUP_BUY_DAILY_RECONCILIATION" ||
    value.snapshot.kind === "GROUP_BUY_RECONCILIATION_RESOLUTION"
  ) {
    return (
      value.policy.resource === "ORDER" &&
      value.policy.action === "RECONCILE_GROUP_BUY" &&
      (value.applicant.role === "OWNER" || value.applicant.role === "MANAGER")
    );
  }
  if (value.snapshot.kind === "CASH_SHIFT_DIFFERENCE") {
    return (
      value.policy.resource === "SHIFT" &&
      value.policy.action === "HANDOVER" &&
      (value.applicant.role === "CASHIER" || value.applicant.role === "MANAGER")
    );
  }
  if (value.snapshot.kind === "MANUAL_REVIEW_RESOLUTION") {
    return (
      value.policy.resource === "MANUAL_REVIEW" &&
      value.policy.action === "RESOLVE" &&
      (value.applicant.role === "OWNER" || value.applicant.role === "MANAGER")
    );
  }
  return (
    value.policy.resource === "PAYMENT" &&
    value.policy.action === "REQUEST_REFUND" &&
    value.applicant.role === "CASHIER"
  );
}

function isCanonicalApprovalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function approvalStepsMatchRequirement(value: ApprovalRequestReadModel): boolean {
  const matchingEffect =
    value.policy.requirement === "MANAGER_OR_OWNER"
      ? "MANAGER_OR_OWNER_APPROVAL"
      : value.policy.requirement === "MANAGER_THEN_OWNER"
        ? "MANAGER_THEN_OWNER_APPROVAL"
        : "OWNER_APPROVAL";
  if (value.policy.effect !== matchingEffect) return false;
  if (value.policy.requirement === "MANAGER_OR_OWNER") {
    if (value.steps.length !== 1 || value.steps[0]?.requiredRole !== "MANAGER_OR_OWNER") {
      return false;
    }
  } else if (value.policy.requirement === "OWNER") {
    if (value.steps.length !== 1 || value.steps[0]?.requiredRole !== "OWNER") return false;
  } else if (
    value.steps.length !== 2 ||
    value.steps[0]?.requiredRole !== "MANAGER" ||
    value.steps[1]?.requiredRole !== "OWNER"
  ) {
    return false;
  }
  return value.steps.every((step, index) => {
    if (step.stepNumber !== index + 1) return false;
    const decided = step.status === "APPROVED" || step.status === "REJECTED";
    return decided
      ? step.decidedBy !== null && step.decidedAt !== null && step.reason !== null
      : step.decidedBy === null && step.decidedAt === null && step.reason === null;
  });
}

function approvalStatusIsConsistent(value: ApprovalRequestReadModel): boolean {
  const statuses = value.steps.map((step) => step.status);
  switch (value.status) {
    case "PENDING_MANAGER_OR_OWNER":
      return statuses.length === 1 && statuses[0] === "AVAILABLE";
    case "PENDING_MANAGER":
      return statuses[0] === "AVAILABLE" && statuses[1] === "WAITING";
    case "PENDING_OWNER":
      return value.policy.requirement === "OWNER"
        ? statuses.length === 1 && statuses[0] === "AVAILABLE"
        : statuses[0] === "APPROVED" && statuses[1] === "AVAILABLE";
    case "APPROVED":
      return statuses.every((status) => status === "APPROVED");
    case "REJECTED":
      return statuses.includes("REJECTED");
    case "CANCELLED":
      return statuses.every((status) => status === "APPROVED" || status === "CANCELLED");
  }
}

export function isCreateOrderCashRefundApprovalCommand(
  value: unknown,
): value is CreateOrderCashRefundApprovalCommand {
  return Value.Check(CreateOrderCashRefundApprovalCommandSchema, value);
}

export function isCreateMemberBalanceRefundApprovalCommand(
  value: unknown,
): value is CreateMemberBalanceRefundApprovalCommand {
  return Value.Check(CreateMemberBalanceRefundApprovalCommandSchema, value);
}

export function isCreatePaidOrderItemReturnApprovalCommand(
  value: unknown,
): value is CreatePaidOrderItemReturnApprovalCommand {
  return Value.Check(CreatePaidOrderItemReturnApprovalCommandSchema, value);
}

export function isCreateInventoryOperationApprovalCommand(
  value: unknown,
): value is CreateInventoryOperationApprovalCommand {
  return (
    Value.Check(CreateInventoryOperationApprovalCommandSchema, value) &&
    inventoryOperationQuantityIsValid(value.operationType, value.quantity)
  );
}

export function isPreviewOrderCashRefundApprovalRequest(
  value: unknown,
): value is PreviewOrderCashRefundApprovalRequest {
  return Value.Check(PreviewOrderCashRefundApprovalRequestSchema, value);
}

export function isPreviewMemberBalanceRefundApprovalRequest(
  value: unknown,
): value is PreviewMemberBalanceRefundApprovalRequest {
  return Value.Check(PreviewMemberBalanceRefundApprovalRequestSchema, value);
}

export function isPreviewPaidOrderItemReturnApprovalRequest(
  value: unknown,
): value is PreviewPaidOrderItemReturnApprovalRequest {
  return Value.Check(PreviewPaidOrderItemReturnApprovalRequestSchema, value);
}

export function isPreviewInventoryOperationApprovalRequest(
  value: unknown,
): value is PreviewInventoryOperationApprovalRequest {
  return Value.Check(PreviewInventoryOperationApprovalRequestSchema, value);
}

export function isOrderCashRefundApprovalPreviewReadModel(
  value: unknown,
): value is OrderCashRefundApprovalPreviewReadModel {
  if (!Value.Check(OrderCashRefundApprovalPreviewReadModelSchema, value)) return false;
  if (!isOrderNumberForOrderId(value.orderNumber, value.orderId, value.businessDate)) return false;
  if (!isCanonicalApprovalTimestamp(value.generatedAt)) return false;
  if (value.orderAlreadyRefundedFen > value.orderPaidFen) return false;
  if (value.canSubmit) {
    return value.blocker === null && value.amountFen <= value.paymentAvailableFen;
  }
  return value.blocker !== null;
}

export function isMemberBalanceRefundApprovalPreviewReadModel(
  value: unknown,
): value is MemberBalanceRefundApprovalPreviewReadModel {
  if (!Value.Check(MemberBalanceRefundApprovalPreviewReadModelSchema, value)) return false;
  if (!isOrderNumberForBusinessDate(value.orderNumber, value.businessDate)) return false;
  if (
    !isCanonicalApprovalTimestamp(value.transactionOccurredAt) ||
    !isCanonicalApprovalTimestamp(value.generatedAt)
  ) {
    return false;
  }
  const maximumRefundableFen = value.originalWalletAmountFen - value.alreadyRefundedFen;
  if (
    !Number.isSafeInteger(maximumRefundableFen) ||
    maximumRefundableFen < 0 ||
    value.refundableRemainingFen > maximumRefundableFen
  ) {
    return false;
  }
  const allocationsAreValid =
    value.refundAllocations.length === 0
      ? !value.canSubmit
      : memberRefundAllocationsAreConsistent(value.refundAllocations, value.amountFen);
  if (!allocationsAreValid) return false;
  if (value.canSubmit) {
    return value.blocker === null && value.amountFen <= value.refundableRemainingFen;
  }
  return value.blocker !== null;
}

export function isMemberBalanceRefundApprovalPreparation(
  value: unknown,
): value is MemberBalanceRefundApprovalPreparation {
  if (!Value.Check(MemberBalanceRefundApprovalPreparationSchema, value)) return false;
  if (!isMemberBalanceRefundApprovalPreviewReadModel(value.preview)) return false;
  const binding = value.executionBinding;
  const sourceTrace = binding.sourceOperationTrace as BusinessOperationTraceV1;
  return (
    isBusinessOperationTraceV1(sourceTrace) &&
    binding.memberId === value.preview.memberId &&
    binding.orderNumber === value.preview.orderNumber &&
    value.businessFactSha256 === value.preview.businessFactSha256 &&
    sourceTrace.businessNumber?.kind === "ORDER" &&
    sourceTrace.businessNumber.value === value.preview.orderNumber
  );
}

export function isPaidOrderItemReturnApprovalPreviewReadModel(
  value: unknown,
): value is PaidOrderItemReturnApprovalPreviewReadModel {
  if (!Value.Check(PaidOrderItemReturnApprovalPreviewReadModelSchema, value)) return false;
  if (!isOrderNumberForOrderId(value.orderNumber, value.orderId, value.businessDate)) return false;
  if (!isCanonicalApprovalTimestamp(value.generatedAt)) return false;
  const allocations = value.refundAllocations;
  const allocationShapeIsValid = allocations
    ? value.paymentSourceCount !== undefined &&
      allocations.length <= value.paymentSourceCount &&
      value.paymentMethod === null &&
      value.refundExecutionMode === null &&
      allocations.reduce((sum, payment) => sum + payment.refundAmountFen, 0) === value.amountFen
    : value.paymentSourceCount === undefined &&
      value.paymentMethod !== null &&
      value.refundExecutionMode !== null;
  if (!allocationShapeIsValid) return false;
  const requestIsExecutable = paidOrderItemReturnAmountsAreConsistent(value);
  return value.canSubmit ? value.blocker === null && requestIsExecutable : value.blocker !== null;
}

export function isPaidOrderItemReturnApprovalPreparation(
  value: unknown,
): value is PaidOrderItemReturnApprovalPreparation {
  if (!Value.Check(PaidOrderItemReturnApprovalPreparationSchema, value)) return false;
  if (!isPaidOrderItemReturnApprovalPreviewReadModel(value.preview)) return false;
  const binding = value.executionBinding as PaidOrderItemSaleReturnApprovalBinding;
  const originalPayments = paidSaleReturnOriginalPayments(binding);
  const previewAllocations = value.preview.refundAllocations;
  const paymentPlanMatches =
    binding.bindingVersion === 1
      ? previewAllocations === undefined &&
        binding.originalPayment.method === value.preview.paymentMethod &&
        binding.originalPayment.executionMode === value.preview.refundExecutionMode
      : previewAllocations !== undefined &&
        JSON.stringify(originalPayments) === JSON.stringify(previewAllocations) &&
        value.preview.paymentMethod === null &&
        value.preview.refundExecutionMode === null;
  return (
    isPaidOrderItemSaleReturnApprovalBinding(binding) &&
    binding.orderId === value.preview.orderId &&
    binding.orderNumber === value.preview.orderNumber &&
    binding.businessDate === value.preview.businessDate &&
    binding.itemId === value.preview.itemId &&
    binding.skuId === value.preview.skuId &&
    binding.productName === value.preview.productName &&
    binding.quantity === value.preview.returnQuantity &&
    binding.returnedQuantityBefore === value.preview.returnedQuantityBefore &&
    binding.returnedQuantityAfter === value.preview.returnedQuantityAfter &&
    binding.orderAmountReductionFen === value.preview.amountFen &&
    binding.physicalDisposition === value.preview.physicalDisposition &&
    binding.inventoryEffect === value.preview.inventoryEffect &&
    paymentPlanMatches &&
    binding.businessFactSha256 === value.businessFactSha256
  );
}

export function isInventoryOperationApprovalPreviewReadModel(
  value: unknown,
): value is InventoryOperationApprovalPreviewReadModel {
  if (!Value.Check(InventoryOperationApprovalPreviewReadModelSchema, value)) return false;
  if (!isCanonicalApprovalTimestamp(value.generatedAt)) return false;
  if (!inventoryOperationCalculationIsConsistent(value)) return false;
  const requestIsExecutable =
    inventoryOperationQuantityIsValid(value.operationType, value.requestedQuantity) &&
    value.quantityDelta !== 0 &&
    (value.allowNegativeStock || value.quantityAfter >= 0);
  const captureIsConsistent =
    value.operationType === "COUNT_SET"
      ? value.lossCapture === undefined &&
        (value.countCapture === undefined ||
          (isInventoryCountCapture(value.countCapture) &&
            value.countCapture.countedQuantity === value.requestedQuantity &&
            value.countCapture.theoreticalQuantity === value.quantityBefore &&
            value.countCapture.surplusUnitCostFen === value.unitCostFen))
      : value.operationType === "LOSS_OUT"
        ? value.countCapture === undefined &&
          (value.lossCapture === undefined ||
            (value.unitCostFen === null &&
              isInventoryLossCapture(value.lossCapture) &&
              value.lossCapture.quantity === value.requestedQuantity &&
              value.lossCapture.quantityBefore === value.quantityBefore &&
              value.lossCapture.quantityAfter === value.quantityAfter))
        : value.countCapture === undefined && value.lossCapture === undefined;
  return (
    captureIsConsistent &&
    (value.canSubmit ? value.blocker === null && requestIsExecutable : value.blocker !== null)
  );
}

export function isInventoryOperationApprovalPreparation(
  value: unknown,
): value is InventoryOperationApprovalPreparation {
  if (!Value.Check(InventoryOperationApprovalPreparationSchema, value)) return false;
  return (
    isInventoryOperationApprovalPreviewReadModel(value.preview) &&
    value.executionBinding.skuId === value.preview.skuId &&
    value.executionBinding.quantityBefore === value.preview.quantityBefore &&
    JSON.stringify(value.executionBinding.countCapture) ===
      JSON.stringify(value.preview.countCapture) &&
    JSON.stringify(value.executionBinding.lossCapture) ===
      JSON.stringify(value.preview.lossCapture) &&
    value.businessFactSha256 === value.preview.businessFactSha256
  );
}

export function isApprovalRequestReadModel(value: unknown): value is ApprovalRequestReadModel {
  if (!Value.Check(ApprovalRequestReadModelSchema, value)) return false;
  if (!isApprovalOperationSnapshotConsistent(value.snapshot)) return false;
  if (!approvalPolicyAndApplicantMatchOperation(value)) return false;
  if (
    !isCanonicalApprovalTimestamp(value.requestedAt) ||
    !isCanonicalApprovalTimestamp(value.updatedAt) ||
    value.steps.some(
      (step) => step.decidedAt !== null && !isCanonicalApprovalTimestamp(step.decidedAt),
    ) ||
    value.timeline.some((event) => !isCanonicalApprovalTimestamp(event.occurredAt)) ||
    (value.execution.lastAttemptAt !== null &&
      !isCanonicalApprovalTimestamp(value.execution.lastAttemptAt)) ||
    (value.execution.completedAt !== null &&
      !isCanonicalApprovalTimestamp(value.execution.completedAt))
  ) {
    return false;
  }
  if (!approvalStepsMatchRequirement(value) || !approvalStatusIsConsistent(value)) return false;
  if (
    value.timeline[0]?.type !== "REQUESTED" ||
    new Set(value.timeline.map((event) => event.eventId)).size !== value.timeline.length
  ) {
    return false;
  }
  if (value.status.startsWith("PENDING_")) {
    return value.execution.status === "AWAITING_APPROVAL";
  }
  if (value.status === "REJECTED" || value.status === "CANCELLED") {
    return value.execution.status === "NOT_APPLICABLE";
  }
  return !["AWAITING_APPROVAL", "NOT_APPLICABLE"].includes(value.execution.status);
}

export function isApprovalRequestListReadModel(
  value: unknown,
): value is ApprovalRequestListReadModel {
  if (!Value.Check(ApprovalRequestListReadModelSchema, value)) return false;
  if (
    !isCanonicalApprovalTimestamp(value.generatedAt) ||
    value.items.some((item) => !isCanonicalApprovalTimestamp(item.requestedAt))
  ) {
    return false;
  }
  if (new Set(value.items.map((item) => item.approvalId)).size !== value.items.length) return false;
  if (
    value.pendingCount < value.items.filter((item) => item.status.startsWith("PENDING_")).length
  ) {
    return false;
  }
  return value.items.every((item) => {
    if (
      item.summary.kind === "INVENTORY_OPERATION" &&
      (item.summary.quantityDelta === 0 || !Number.isSafeInteger(item.summary.quantityAfter))
    ) {
      return false;
    }
    if (
      item.summary.kind === "PURCHASE_ORDER_APPROVAL" &&
      (!Number.isSafeInteger(item.summary.subtotalFen + item.summary.taxFen) ||
        item.summary.totalFen !== item.summary.subtotalFen + item.summary.taxFen)
    ) {
      return false;
    }
    if (
      item.summary.kind === "GROUP_BUY_DAILY_RECONCILIATION" &&
      (item.summary.confirmedCount > item.summary.fulfillmentCount ||
        item.summary.exceptionCount < item.summary.fulfillmentCount - item.summary.confirmedCount)
    ) {
      return false;
    }
    if (
      item.summary.kind === "MANUAL_REVIEW_RESOLUTION" &&
      !manualReviewResolutionCodeMatchesCategory(item.summary.category, item.summary.resolutionCode)
    ) {
      return false;
    }
    if (item.status.startsWith("PENDING_")) return item.executionStatus === "AWAITING_APPROVAL";
    if (item.status === "REJECTED" || item.status === "CANCELLED") {
      return item.executionStatus === "NOT_APPLICABLE";
    }
    return !["AWAITING_APPROVAL", "NOT_APPLICABLE"].includes(item.executionStatus);
  });
}

export function isApprovalListView(value: unknown): value is ApprovalListView {
  return Value.Check(ApprovalListViewSchema, value);
}

export function isApprovalSummaryReadModel(value: unknown): value is ApprovalSummaryReadModel {
  return (
    Value.Check(ApprovalSummaryReadModelSchema, value) &&
    isCanonicalApprovalTimestamp(value.generatedAt)
  );
}

export function isDecideApprovalCommand(value: unknown): value is DecideApprovalCommand {
  return Value.Check(DecideApprovalCommandSchema, value);
}

export function isCancelApprovalCommand(value: unknown): value is CancelApprovalCommand {
  return Value.Check(CancelApprovalCommandSchema, value);
}

export function isRetryApprovalExecutionCommand(
  value: unknown,
): value is RetryApprovalExecutionCommand {
  return Value.Check(RetryApprovalExecutionCommandSchema, value);
}

export function isExecutePaidOrderItemReturnCommand(
  value: unknown,
): value is ExecutePaidOrderItemReturnCommand {
  if (!Value.Check(ExecutePaidOrderItemReturnCommandSchema, value)) return false;
  const command = value as ExecutePaidOrderItemReturnCommand;
  if (command.externalRefundReferences === undefined) return true;
  return (
    command.externalRefundReference === null &&
    new Set(command.externalRefundReferences.map((item) => item.paymentId)).size ===
      command.externalRefundReferences.length
  );
}

export function isCreateApprovalRequestInternalCommand(
  value: unknown,
): value is CreateApprovalRequestInternalCommand {
  if (!Value.Check(CreateApprovalRequestInternalCommandSchema, value)) return false;
  const syntheticReadModel = {
    snapshot: value.snapshot,
    policy: value.policy,
    applicant: value.applicant,
  } as ApprovalRequestReadModel;
  return (
    isApprovalOperationSnapshotConsistent(value.snapshot) &&
    approvalPolicyAndApplicantMatchOperation(syntheticReadModel) &&
    isCanonicalApprovalTimestamp(value.occurredAt) &&
    value.applicant.role !== "SYSTEM" &&
    value.applicantRoles.includes(value.applicant.role) &&
    (value.policy.requirement === "MANAGER_OR_OWNER"
      ? value.policy.effect === "MANAGER_OR_OWNER_APPROVAL"
      : value.policy.requirement === "MANAGER_THEN_OWNER"
        ? value.policy.effect === "MANAGER_THEN_OWNER_APPROVAL"
        : value.policy.effect === "OWNER_APPROVAL")
  );
}
